import { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getStripe } from './stripeLoader';

// ── Inner form (must be a child of <Elements>) ──────────────────────────────

interface InnerFormProps {
  /** Absolute URL — Stripe redirects here after card / 3DS / Apple Pay flow */
  returnUrl: string;
  /** External "submit disabled" reason — e.g. timer expired */
  disabled?: boolean;
  /** Total to display on the pay button */
  totalLabel: string;
}

function InnerForm({ returnUrl, disabled, totalLabel }: InnerFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || disabled || submitting) return;

    setSubmitting(true);
    setError(null);
    console.log('[payment] stripe.confirmPayment → returnUrl:', returnUrl);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    // Only reaches this branch on validation/auth error — success triggers a redirect
    if (stripeError) {
      console.error('[payment] stripe.confirmPayment error:', stripeError);
      setError(stripeError.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
    }
    // Note: do NOT setSubmitting(false) on success — the redirect is in flight
  };

  const buttonDisabled = !stripe || !elements || disabled || submitting;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <PaymentElement options={{ wallets: { applePay: 'auto', googlePay: 'auto' } }} />

      {error && (
        <Box
          sx={{
            p: 1.25,
            bgcolor: '#FEE2E2',
            border: '1px solid #FCA5A5',
            borderRadius: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: '#991B1B', fontWeight: 600 }}>
            {error}
          </Typography>
        </Box>
      )}

      <MuiButton
        type="submit"
        variant="contained"
        size="large"
        disabled={buttonDisabled}
        startIcon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
        sx={{
          bgcolor: '#111827',
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 15,
          '&:hover': { bgcolor: '#1F2937' },
          '&.Mui-disabled': { bgcolor: '#9CA3AF', color: '#fff' },
        }}
      >
        {submitting ? 'Processing…' : `Pay ${totalLabel}`}
      </MuiButton>

      <Typography sx={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
        Payments processed securely by Stripe. Your card details never touch our servers.
      </Typography>
    </Box>
  );
}

// ── Public wrapper — handles <Elements> setup ───────────────────────────────

interface StripePaymentFormProps {
  clientSecret: string;
  returnUrl: string;
  totalLabel: string;
  disabled?: boolean;
}

const stripePromise: Promise<Stripe | null> = getStripe();

export default function StripePaymentForm({
  clientSecret,
  returnUrl,
  totalLabel,
  disabled,
}: StripePaymentFormProps) {
  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#111827',
        borderRadius: '8px',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <InnerForm returnUrl={returnUrl} disabled={disabled} totalLabel={totalLabel} />
    </Elements>
  );
}
